// ─── Datos del Proyecto ──────────────────────────────────────────────────────
// Componente que recoge los 48 campos de la pestaña "Detalles de evaluación"
// de la Herramienta Maestra BREEAM ES NC 2015 (Ed.18).
//
// Estos datos:
// - se guardan en evaluations.criteria_code='PROJECT-DETAILS' (jsonb)
// - alimentan el botón futuro "Generar Herramienta Maestra" (rellena la pestaña
//   Detalles de evaluación con estos valores)
// - los toggles condicionales marcarán criterios como NO APLICA en cascada
//   (se implementará en sesión siguiente)
//
// Las listas de opciones (TIPO_EDIFICIO, PAISES, etc.) se extrajeron del Excel
// para que coincidan exactamente con las celdas de la herramienta maestra.

import { useState, useEffect, useCallback, useRef } from 'react'
import { Buildings, FloppyDisk, CaretDown, CaretRight, Info, X } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import {
  checkZARI, checkIGMEBDMoves, checkErupcionesHistoricas,
  checkPerillIncendi, checkRiscGreu,
  checkPGA475, checkEMS98,
} from '../lib/wmsQuery'
import { getCCAA } from './informes/syb7Sources'

// ── Opciones de selectores (mapeo 1:1 con la herramienta maestra) ────────────
const TIPO_EDIFICIO_PRINCIPAL = [
  'Oficinas', 'Industrial', 'Comercial', 'Docente', 'Sanitario',
  'Justicia', 'Multiresidencial', 'Hoteles', 'Otros edificios',
]

const TIPO_EDIFICIO_SUBGRUPO = [
  'Industrial - Proceso / fabricación',
  'Industrial - Mantenimiento de vehículos',
  'Industrial - almacenamiento / distribución',
  'Oficinas - Edificios de oficinas generales',
  'Oficinas - Oficinas con zonas de investigación y desarrollo',
  'Comercial - Café',
  'Comercial - establecimientos de bebidas',
  'Comercial - Comida preparada para llevar',
  'Comercial - Restaurante',
  'Comercial - Concesionarios',
  'Comercial - proveedor de servicios <<extrabursátiles>>',
  'Comercial - Tienda',
  'Comercial - Centro comercial',
  'Comercial - Showroom',
  'Docente  - Educación infantil',
  'Docente - Educación primaria',
  'Docente - Educación secundaria',
  'Docente - Educación superior',
  'Sanitario - Consultorio médico',
  'Sanitario - Centro de salud',
  'Sanitario - Clínica',
  'Sanitario - Hospital',
  'Justicia - Juzgados',
  'Justicia - Audiencias provinciales y Tribunales',
  'Justicia - Juzgados combinados',
  'Multiresidencial - Residencia de ancianos',
  'Multiresidencial - Residencia universitaria',
  'Otros - hotel, hostal, pensión',
  'Otros - galería de arte / museo',
  'Otros - Biblitoeca',
  'Otros - Centro de día, centro cívico, centro comunitario',
  'Otros - Lugar de culto',
  'Otros - cine',
  'Otros - Teatro, sala de conciertos',
  'Otros - Salas de exhibición y conferencias',
  'Otros - Deportes al aire libre e interiro',
  'Otros - Gimnasios',
  'Otros - Zonas de recreo (con o sin piscina)',
  'Otros - Centro de transporte',
  'Otros - Centro de investigación y desarrollo (laboratorios 2 y 3)',
  'Otros - Guarderías',
  'Otros.',
]

const TIPO_PROYECTO = [
  'Nueva construcción (acondicionado)',
  'Nueva Construcción (núcleo y envolvente) ',
  'Rehabilitación mayor (acondicionado)',
  'Rehabilitación mayor (núcleo y envolvente)',
]

const FASE_EVALUACION = [
  'Diseño (provisional) ',
  'Posconstrucción (final)',
]

const TIPO_LOCALIZACION = [
  'Ciudad grande >1m población',
  'Área metropolitana construida (>250,000 población)',
  'Áreas urbanas grandes (>250,000 población)',
  'Áreas urbanas medias (>25,000 población)',
  'Áreas urbanas pequeñas (>10,000 población)',
  'Pueblo (>3000 población)',
  'Pueblo (<3000 población)',
]

// Köppen-Geiger global (la que exige la celda J54 del Excel BREEAM maestro)
const ZONA_CLIMATICA = [
  'A. Equatorial', 'B. Arid', 'C. Warm Temperate', 'D. Snow', 'E. Polar',
]

// CTE-DB-HE española — las 14 zonas climáticas reales que usan los proyectos en España
const ZONA_CLIMATICA_CTE = [
  'α3', 'α4',                          // Canarias costa
  'A3', 'A4',                          // sur peninsular, Tenerife capital
  'B3', 'B4',                          // Sevilla, Murcia
  'C1', 'C2', 'C3', 'C4',              // costa atlántica, meseta
  'D1', 'D2', 'D3',                    // interior frío (Castilla, Aragón)
  'E1',                                 // montaña (Pirineos, Picos)
]

// Mapeo CTE → Köppen-Geiger sugerido (el usuario puede sobrescribirlo si quiere)
const CTE_TO_KOPPEN = {
  'α3': 'C. Warm Temperate', 'α4': 'C. Warm Temperate',
  'A3': 'C. Warm Temperate', 'A4': 'C. Warm Temperate',
  'B3': 'C. Warm Temperate', 'B4': 'C. Warm Temperate',
  'C1': 'C. Warm Temperate', 'C2': 'C. Warm Temperate',
  'C3': 'C. Warm Temperate', 'C4': 'C. Warm Temperate',
  'D1': 'C. Warm Temperate', 'D2': 'C. Warm Temperate', 'D3': 'C. Warm Temperate',
  'E1': 'D. Snow',
}

const SISTEMA_CALEFACCION = [
  'Sistema por agua', 'Sistema por aire',
  'Otros tipos de sistemas de calefacción', 'Ninguno',
]
const SISTEMA_REFRIGERACION = [
  'Aire acondicionado', 'Otro tipo de refrigeración', 'Ninguna',
]
const SISTEMA_ACS = [
  'Suministro y distribución centralizada', 'Punto de uso', 'Otro', 'Ninguno',
]
const CONTROLES = [
  'Controles standard (tiempo/temperatura)', 'Otro', 'Ninguno',
]

const SI_NO = ['Si', 'No']

const LABORATORIOS = [
  'Sin laboratorio',
  'Laboratorio  <10% de la superficie total del edificio',
  'Laboratorio ≥10% - <25% de la superficie total del edificio',
  'Laboratorio ≥25% de la superficie total del edificio',
]
const NIVEL_CONTENCION = [
  'No hay laboratorios',
  'Nivel de contención 1',
  'Nivel de contención 2',
  'Nivel de contención 3',
]

const ESQUEMAS = ['Nueva Construcción']
const VERSIONES = ['SD5075 Issue 0.0', 'SD5075 Issue 1.0']

const PAISES = [
  'Afghanistan','Albania','Algeria','Angola','Antigua & Barbuda','Argentina','Armenia','Australia','Austria','Azerbaijan','Bahrain','Bangladesh','Barbados','Belarus','Belgium','Belize','Benin','Bhutan','Bolivia','Bosnia & Herzegovina','Botswana','Brazil','Brunei','Bulgaria','Burkina Faso','Burundi','Cambodia','Cameroon','Canada','Cape Verde','Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Congo, Dem. Republic',"Cook Islands",'Costa Rica',"Côte d'Ivoire",'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic','Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Ethiopia','Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada','Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras','Hong Kong','Hungary','Iceland','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy','Jamaica','Japan','Jordan','Kazakhstan','Kenya','Kiribati','Korea, Dem. Rep.','Korea, Rep.','Kosovo','Kuwait','Kyrgyz Republic','Laos','Latvia','Lebanon','Lesotho','Liberia','Libya','Liechtenstein','Lithuania','Luxembourg','Macao','Macedonia, FYR','Madagascar','Malawi','Malaysia','Maldives','Mali','Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia, Fed. Sts.','Moldova','Monaco','Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal','Netherlands','New Zealand','Nicaragua','Niger','Nigeria','Niue','Norway','Oman','Pakistan','Palau','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland','Portugal','Qatar','Romania','Russia','Rwanda','Samoa','San Marino','São Tomé and Príncipe','Saudi Arabia','Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovak Republic','Slovenia','Solomon Islands','Somalia','South Africa','South Sudan','Spain','Sri Lanka','St. Kitts & Nevis','St. Lucia','St. Vincent & the Grenadines','Sudan','Suriname','Swaziland','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand','Timor-Leste','Togo','Tonga','Trinidad & Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu','UK','US','Uganda','Ukraine','United Arab Emirates','Uruguay','Uzbekistan','Vanuatu','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe',
]

// ── Estado inicial ───────────────────────────────────────────────────────────
const EMPTY_STATE = {
  // A. Identificación
  codigoBreeam: '',
  cliente: '',
  usuarioFinal: '',
  asesor: '',
  organizacion: '',
  // B. Edificio
  nombreEdificio: '',
  direccion: '',
  codigoPostal: '',
  pais: 'Spain',
  tipoEdificioPrincipal: '',
  tipoEdificioSubgrupo: '',
  superficieConstruida: '',
  superficieUtil: '',
  esquema: 'Nueva Construcción',
  version: 'SD5075 Issue 1.0',
  tipoProyecto: '',
  faseEvaluacion: '',
  // Fechas de fases del proyecto (opcionales · usadas en el Informe de Sostenibilidad)
  fechaInicioDiseno: '',
  fechaFinConstruccion: '',
  fechaPostconstruccion: '',
  // Campos narrativos opcionales del Informe de Sostenibilidad
  unidades: '',
  descripcionProyecto: '',
  tipoLocalizacion: '',
  zonaClimaticaCTE: '',
  zonaClimatica: '',
  sistemaCalefaccion: '',
  sistemaRefrigeracion: '',
  sistemaACS: '',
  controles: '',
  // C. Ubicación geográfica (se sincroniza también en la tabla projects)
  latitude: '',
  longitude: '',
  municipality: '',
  province: '',
  ineCode: '',
  seismicAb: '',
  arpsiStatus: '',
  ubicacionLastCheck: '',
  // D. Toggles condicionales
  zonaIndustrialClimatizada: '',
  edificioSanitarioPacientes: '',
  industrialZonaOficinas: '',
  prohibePAO: 'Si',
  inquilinosMultiples: '',
  refrigeracionComercial: '',
  zonasExternas: '',
  vegetacionPaisaje: '',
  riesgoPeligrosNaturales: '',
  lavadoVehiculos: '',
  ascensoresEscaleras: '',
  piscinas: '',
  laboratorios: '',
  nivelContencionLab: '',
  dispositivosContencionLab: '',
  // E. Equipo
  promotor: '',
  constructor: '',
  arquitecto: '',
  projectManagement: '',
  instalaciones: '',
  breeamAP: '',
  otroMiembro1: '',
  otroMiembro2: '',
  otroMiembro3: '',
  otroMiembro4: '',
}

// ── Helpers BD ───────────────────────────────────────────────────────────────
async function saveDataToSupabase(projectId, data) {
  try {
    const { data: { user }, error: authErr } = await supabase.auth.getUser()
    if (authErr || !user) return
    const { error } = await supabase.from('evaluations').upsert({
      project_id: projectId,
      user_id: user.id,
      category: 'Proyecto',
      criteria_code: 'PROJECT-DETAILS',
      criteria_name: 'Datos del Proyecto',
      points_obtained: 0, points_max: 0, cumple: false,
      justificacion: JSON.stringify(data),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id,category,criteria_code' })
    if (error) console.error('PROJECT-DETAILS save error:', error.message)

    // Sincronizar campos geográficos como columnas nativas de projects,
    // para que InformesTecnicos y otros consumidores puedan leerlos sin
    // pasar por operational_iro_settings.
    const geo = {
      latitude:     data.latitude     === '' ? null : Number(data.latitude),
      longitude:    data.longitude    === '' ? null : Number(data.longitude),
      municipality: data.municipality || null,
      province:     data.province     || null,
      ine_code:     data.ineCode      || null,
      updated_at:   new Date().toISOString(),
    }
    const { error: geoErr } = await supabase.from('projects').update(geo).eq('id', projectId)
    if (geoErr) console.error('PROJECT-DETAILS geo sync error:', geoErr.message)
  } catch (e) { console.error('save exception:', e.message) }
}

async function loadDataFromSupabase(projectId) {
  try {
    // Cargar en paralelo el JSON del formulario y las columnas geográficas del proyecto
    const [evalRes, projRes] = await Promise.all([
      supabase.from('evaluations')
        .select('justificacion').eq('project_id', projectId)
        .eq('criteria_code', 'PROJECT-DETAILS').maybeSingle(),
      supabase.from('projects')
        .select('latitude, longitude, municipality, province, ine_code')
        .eq('id', projectId).maybeSingle(),
    ])
    let parsed = null
    if (evalRes?.data?.justificacion) {
      try { parsed = JSON.parse(evalRes.data.justificacion) } catch {}
    }
    if (projRes?.data) {
      // Los valores de la tabla projects tienen prioridad sobre el JSON del
      // formulario (fuente única de la verdad para los campos geográficos).
      const p = projRes.data
      parsed = { ...(parsed || {}) }
      if (p.latitude     != null) parsed.latitude     = String(p.latitude)
      if (p.longitude    != null) parsed.longitude    = String(p.longitude)
      if (p.municipality != null) parsed.municipality = p.municipality
      if (p.province     != null) parsed.province     = p.province
      if (p.ine_code     != null) parsed.ineCode      = p.ine_code
    }
    return parsed
  } catch (e) {}
  return null
}

// ── Reglas de exclusión automática ───────────────────────────────────────────
// Cada regla evalúa state (PROJECT-DETAILS) y devuelve true si el criterio
// debe marcarse como NO APLICA automáticamente.
//
// Diseño defensivo:
//  - El marcador `exclusion_origen: 'PROJECT-DETAILS'` en justificacion permite
//    distinguir un NA derivado del formulario de un NA manual del asesor.
//  - Si el criterio ya tenía puntos > 0, NUNCA se sobrescribe (alerta al user).
//  - Si el toggle vuelve atrás, sólo se revierten los NA con origen
//    'PROJECT-DETAILS'. Los NA manuales nunca se tocan.
const EXCLUSION_RULES = [
  { code: 'SyB 8', category: 'Salud y Bienestar',
    when: s => s.piscinas === 'No',
    reason: 'No hay piscinas en el edificio (Datos del Proyecto · toggle "piscinas = No"). SyB 8 trata el tratamiento sostenible del agua de piscinas, por lo que no aplica al alcance del proyecto.' },
  { code: 'ENE 5', category: 'Energía',
    when: s => s.refrigeracionComercial === 'No',
    reason: 'No hay refrigeración comercial/industrial ni almacenamiento en frío (Datos del Proyecto · toggle "refrigeracionComercial = No"). ENE 5 evalúa la conservación frigorífica, fuera del alcance.' },
  { code: 'ENE 6', category: 'Energía',
    when: s => s.ascensoresEscaleras === 'No',
    reason: 'No hay sistemas de transporte de personas (Datos del Proyecto · toggle "ascensoresEscaleras = No"). ENE 6 evalúa ascensores/escaleras mecánicas, no aplicable.' },
  { code: 'ENE 7', category: 'Energía',
    when: s => s.laboratorios === 'Sin laboratorio',
    reason: 'No hay laboratorios en el edificio (Datos del Proyecto · toggle "laboratorios = Sin laboratorio"). ENE 7 evalúa la eficiencia energética de sistemas de laboratorio, fuera del alcance.' },
  { code: 'USE 2', category: 'Uso del Suelo y Ecología',
    when: s => s.zonasExternas === 'No',
    reason: 'No hay zonas externas dentro del emplazamiento (Datos del Proyecto · toggle "zonasExternas = No"). USE 2 evalúa el valor ecológico del emplazamiento, no aplicable.' },
  { code: 'USE 4', category: 'Uso del Suelo y Ecología',
    when: s => s.zonasExternas === 'No',
    reason: 'No hay zonas externas dentro del emplazamiento. USE 4 (mejora ecológica) no aplicable.' },
  { code: 'USE 5', category: 'Uso del Suelo y Ecología',
    when: s => s.zonasExternas === 'No',
    reason: 'No hay zonas externas dentro del emplazamiento. USE 5 (impacto a largo plazo sobre la biodiversidad) no aplicable.' },
  { code: 'USE 7', category: 'Uso del Suelo y Ecología',
    when: s => s.zonasExternas === 'No',
    reason: 'No hay zonas externas dentro del emplazamiento. USE 7 (control de erosión) no aplicable.' },
  { code: 'AG 4', category: 'Agua',
    when: s => s.lavadoVehiculos === 'No' && (s.vegetacionPaisaje === 'No' || s.zonasExternas === 'No'),
    reason: 'AG 4 evalúa riego de zonas verdes + lavado de vehículos. El proyecto no incluye lavado de vehículos (lavadoVehiculos = No) ni zonas verdes externas con riego (vegetacionPaisaje/zonasExternas = No), por lo que el criterio no aplica.' },
  { code: 'SyB 2', category: 'Salud y Bienestar', partial: true,
    when: s => s.laboratorios === 'Sin laboratorio',
    affectsSubcriteriaFrom: 12,
    reason: 'No hay laboratorios. SyB 2 Parte 2 (Criterios 12-18: vitrinas de gases y zonas de contención) no aplicable. La Parte 1 (Criterios 1-11) sí aplica.' },
]

// Carga el estado actual de los criterios afectados por las reglas
async function fetchAffectedCriteria(projectId) {
  const codes = [...new Set(EXCLUSION_RULES.map(r => r.code))]
  const { data, error } = await supabase
    .from('evaluations')
    .select('id, category, criteria_code, points_obtained, points_max, cumple, justificacion, subcriteria_cumplidos, subcriteria_total')
    .eq('project_id', projectId)
    .in('criteria_code', codes)
  if (error) { console.error('fetchAffected error:', error); return [] }
  return data || []
}

// Aplica las exclusiones automáticas. Devuelve { applied, reverted, skipped, errors }.
async function applyAutoExclusions(projectId, state) {
  const result = { applied: [], reverted: [], skipped: [], errors: [] }
  if (!projectId || !state) return result

  const { data: { user } } = await supabase.auth.getUser()
  if (!user) { result.errors.push('No hay sesión'); return result }

  const existing = await fetchAffectedCriteria(projectId)
  const existingByCode = new Map(existing.map(e => [e.criteria_code, e]))

  for (const rule of EXCLUSION_RULES) {
    const should = rule.when(state)
    const ev = existingByCode.get(rule.code)
    let just = {}
    try { just = ev?.justificacion ? JSON.parse(ev.justificacion) : {} } catch {}

    // Caso 1: la regla dice "marcar NA" y el criterio aún no está NA
    if (should && !just.no_aplica) {
      if ((ev?.points_obtained || 0) > 0) {
        result.skipped.push({ code: rule.code, reason: `Criterio tiene ${ev.points_obtained}/${ev.points_max} pts: NO se marca NA automáticamente para no perder evaluación` })
        continue
      }
      // Marcar NA
      const newJust = {
        ...just,
        no_aplica: true,
        fecha_no_aplica: new Date().toISOString(),
        exclusion_origen: 'PROJECT-DETAILS',
        exclusion_motivo: rule.reason,
      }
      const upsertData = {
        project_id: projectId,
        user_id: user.id,
        category: rule.category,
        criteria_code: rule.code,
        criteria_name: ev?.criteria_name || rule.code,
        points_obtained: 0,
        points_max: ev?.points_max || 0,
        cumple: false,
        justificacion: JSON.stringify(newJust),
        subcriteria_cumplidos: 0,
        subcriteria_total: ev?.subcriteria_total || 0,
        updated_at: new Date().toISOString(),
      }
      const { error } = await supabase.from('evaluations').upsert(upsertData, { onConflict: 'project_id,category,criteria_code' })
      if (error) result.errors.push(`${rule.code}: ${error.message}`)
      else result.applied.push({ code: rule.code, motivo: rule.reason })
      continue
    }

    // Caso 2: la regla dice "NO debe ser NA" pero está NA con origen PROJECT-DETAILS → revertir
    if (!should && just.no_aplica && just.exclusion_origen === 'PROJECT-DETAILS') {
      const newJust = { ...just }
      delete newJust.no_aplica
      delete newJust.fecha_no_aplica
      delete newJust.exclusion_origen
      delete newJust.exclusion_motivo
      const upsertData = {
        project_id: projectId,
        user_id: user.id,
        category: rule.category,
        criteria_code: rule.code,
        criteria_name: ev?.criteria_name || rule.code,
        points_obtained: 0,
        points_max: ev?.points_max || 0,
        cumple: false,
        justificacion: JSON.stringify(newJust),
        subcriteria_cumplidos: 0,
        subcriteria_total: ev?.subcriteria_total || 0,
        updated_at: new Date().toISOString(),
      }
      const { error } = await supabase.from('evaluations').upsert(upsertData, { onConflict: 'project_id,category,criteria_code' })
      if (error) result.errors.push(`${rule.code}: ${error.message}`)
      else result.reverted.push({ code: rule.code, motivo: 'El toggle correspondiente ha cambiado: el criterio vuelve a aplicar y queda pendiente de evaluación.' })
      continue
    }

    // Caso 3: NA con origen distinto (manual) — nunca tocar
    // Caso 4: should & ya estaba NA → idempotente, nada que hacer
  }

  // Recalcular total_points del proyecto
  try {
    const { data: allEvs } = await supabase
      .from('evaluations').select('points_obtained, criteria_code')
      .eq('project_id', projectId)
    const total = (allEvs || [])
      .filter(e => !e.criteria_code.endsWith('-DATA') && !e.criteria_code.endsWith('-PARTIDAS') && !e.criteria_code.endsWith('-CERTS') && e.criteria_code !== 'PROJECT-DETAILS')
      .reduce((s, e) => s + (e.points_obtained || 0), 0)
    await supabase.from('projects').update({ total_points: total, updated_at: new Date().toISOString() }).eq('id', projectId)
  } catch (e) { console.warn('Recalc total_points fallo:', e.message) }

  return result
}

// ── Estilos comunes ──────────────────────────────────────────────────────────
const C = {
  bg: '#FAF7F3', card: 'white', border: '#DDD5C8', text: '#3B2F2F',
  muted: '#7A6A5A', accent: '#1A3A4E', warn: '#D4614A', olive: '#8D9A2E',
  blueLight: '#EBF3F8', amber: '#C9A961',
}
const FF = "'DM Sans', sans-serif"
const LABEL_STYLE = { fontSize: '0.78rem', color: C.muted, fontWeight: '500', marginBottom: '0.25rem', display: 'block', fontFamily: FF }
const INPUT_STYLE = {
  width: '100%', padding: '0.45rem 0.6rem', fontSize: '0.82rem',
  border: `1px solid ${C.border}`, borderRadius: '4px', background: 'white',
  color: C.text, fontFamily: FF, outline: 'none',
}

// ── Toggle de exclusión: muestra qué criterios afecta ────────────────────────
const TOGGLE_AFFECTS = {
  piscinas:               'Si NO → SyB 8 marcado NO APLICA',
  laboratorios:           'Si "Sin laboratorio" → SyB 2 P2 (C12-C18) + ENE 7 NO APLICA',
  refrigeracionComercial: 'Si NO → ENE 5 NO APLICA',
  zonasExternas:          'Si NO → USE 2 + USE 4 + USE 5 + USE 7 + CNT 3 NO APLICA',
  ascensoresEscaleras:    'Si NO → ENE 6 NO APLICA',
  vegetacionPaisaje:      'Si NO → USE 4 (parte vegetación) + AG 2 (riego)',
  riesgoPeligrosNaturales:'Si NO → CNT 3 (parte peligros naturales) NO APLICA',
  lavadoVehiculos:        'Si NO → parte de AG + CNT correspondiente',
  inquilinosMultiples:    'Si SÍ → activa criterios de GST 4 / GST 5 multi-inquilino',
}

// ── Sección colapsable ───────────────────────────────────────────────────────
function Section({ title, icon: Icon, count, open, onToggle, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '6px', background: C.card, marginBottom: '0.75rem' }}>
      <div
        onClick={onToggle}
        style={{
          padding: '0.875rem 1rem', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '0.6rem',
          background: open ? '#F5F0EA' : 'transparent',
          borderBottom: open ? `1px solid ${C.border}` : 'none',
          fontFamily: FF, fontWeight: '600', color: C.text, fontSize: '0.92rem',
        }}>
        {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
        {Icon && <Icon size={16} color={C.accent} />}
        <span style={{ flex: 1 }}>{title}</span>
        <span style={{ fontSize: '0.74rem', color: C.muted, fontWeight: '400' }}>
          {count} campos
        </span>
      </div>
      {open && <div style={{ padding: '1rem' }}>{children}</div>}
    </div>
  )
}

// ── Campo Input genérico ─────────────────────────────────────────────────────
function Field({ label, children, hint, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}`, minWidth: 0 }}>
      <label style={LABEL_STYLE}>{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: '0.7rem', color: C.muted, marginTop: '0.25rem', fontStyle: 'italic', fontFamily: FF }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function TextInput({ value, onChange, type = 'text', placeholder }) {
  return (
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} style={INPUT_STYLE} />
  )
}

function SelectInput({ value, onChange, options, placeholder = '— Selecciona —' }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={INPUT_STYLE}>
      <option value="">{placeholder}</option>
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  )
}

function ToggleField({ label, value, onChange, affects, options = SI_NO }) {
  return (
    <Field label={label} hint={affects}>
      <SelectInput value={value} onChange={onChange} options={options} />
    </Field>
  )
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function ProjectDetails({ projectId, projectName }) {
  const storageKey = projectId ? `project_details_${projectId}` : null
  const [state, setState] = useState(() => {
    if (!storageKey) return EMPTY_STATE
    try {
      return JSON.parse(localStorage.getItem(storageKey) || 'null') || EMPTY_STATE
    } catch { return EMPTY_STATE }
  })
  const [savedOk, setSavedOk] = useState(false)
  const [applying, setApplying] = useState(false)
  const [applyResult, setApplyResult] = useState(null) // { applied, reverted, skipped, errors }
  const [openSections, setOpenSections] = useState({
    identificacion: true, edificio: true, ubicacion: true, toggles: true, equipo: false,
  })

  // Carga desde Supabase.
  //
  // Bug histórico: si el usuario cambiaba un campo (p. ej. zona climática) antes
  // de que resolviera el fetch, el .then() pisaba su edición con el valor viejo
  // de Supabase. Usamos userTouchedRef para respetar los cambios en curso: si el
  // usuario ya editó, hacemos merge poniendo el remote primero y el estado
  // actual encima (los campos que tocó ganan sobre lo que llega de Supabase).
  const userTouchedRef = useRef(false)
  useEffect(() => {
    if (!projectId) return
    userTouchedRef.current = false
    loadDataFromSupabase(projectId).then(remote => {
      if (!remote) return
      setState(prev => userTouchedRef.current
        ? { ...EMPTY_STATE, ...remote, ...prev }
        : { ...EMPTY_STATE, ...remote })
    })
  }, [projectId])

  // Persistencia: localStorage inmediato + Supabase debounced 1.5s
  const saveTimer = useRef(null)
  useEffect(() => {
    if (!projectId || !storageKey) return
    localStorage.setItem(storageKey, JSON.stringify(state))
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => saveDataToSupabase(projectId, state), 1500)
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current) }
  }, [state, projectId, storageKey])

  // Auto-verificación NCSE-02 + ARPSI cuando cambian ubicación/coordenadas.
  // Se dispara con un debounce independiente de 2 segundos para no consultar
  // en cada tecla.
  const verifyTimer = useRef(null)
  const [verifying, setVerifying] = useState(false)
  useEffect(() => {
    if (!projectId) return
    const hasLatLon = state.latitude !== '' && state.longitude !== ''
    const hasMuni   = (state.municipality || '').trim().length > 2
    if (!hasLatLon && !hasMuni) return

    if (verifyTimer.current) clearTimeout(verifyTimer.current)
    verifyTimer.current = setTimeout(async () => {
      setVerifying(true)
      try {
        const updates = {}

        // Guardaremos aquí todo lo que hay que sincronizar en operational_iro_settings
        const iroExtras = {}

        // ── Reverse geocoding (Nominatim vía servidor) ─────────────────────
        // Si el usuario mete lat/lon, consultamos qué municipio + provincia
        // corresponden. Autorrellenamos los campos si están vacíos y
        // guardamos la sugerencia para poder ofrecerla si están llenos.
        if (hasLatLon) {
          try {
            const lat = Number(state.latitude)
            const lon = Number(state.longitude)
            if (isFinite(lat) && isFinite(lon)) {
              const isProd = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
              const base = isProd ? '/pdf' : 'http://localhost:3010'
              const r = await fetch(`${base}/reverse-geocode?lat=${lat}&lon=${lon}`)
              if (r.ok) {
                const g = await r.json()
                // Autofill: rellenar municipio/provincia solo si están vacíos
                // (respetamos lo que el usuario haya podido escribir a mano).
                if (g.municipality && !(state.municipality || '').trim()) {
                  updates.municipality = g.municipality
                }
                if (g.province && !(state.province || '').trim()) {
                  updates.province = g.province
                }
                // Y guardamos la detección para mostrarla como sugerencia
                updates.reverseGeoSuggestion = {
                  municipality: g.municipality,
                  province: g.province,
                  ccaa: g.ccaa,
                  postcode: g.postcode,
                }
              }
            }
          } catch (err) {
            console.warn('[reverse-geocode] no disponible:', err.message)
          }
        }

        // Municipio y provincia efectivos: usamos el valor autofillado por
        // reverse geocoding si el usuario no había escrito nada; en caso
        // contrario, respetamos lo que ya había.
        const effectiveMuni = (updates.municipality || state.municipality || '').trim()
        const effectiveProv = (updates.province     || state.province     || '').trim()
        const hasEffectiveMuni = effectiveMuni.length > 2

        // NCSE-02 — lookup por municipio (nombre + provincia)
        let ncse02Result = null
        if (hasEffectiveMuni) {
          const { data: seismic, error: sErr } = await supabase.rpc('get_seismic_ab', {
            p_municipio: effectiveMuni,
            p_provincia: effectiveProv || null,
          })
          if (!sErr && seismic && seismic[0]) {
            ncse02Result = seismic[0]
            const r = ncse02Result
            updates.seismicAb = r.aplicable ? String(r.ab_g) : 'NCSE-02 no aplicable (ab < 0,04 g)'
            if (r.aplicable) {
              iroExtras.seismic_ab_ncse02   = Number(r.ab_g)
              iroExtras.seismic_k_coefficient = Number(r.k)
              iroExtras.seismic_check_date  = new Date().toISOString().slice(0, 10)
            }
          }
        }

        // ARPSI — proximidad al eje del tramo (buffer 500 m)
        if (hasLatLon) {
          const lat = Number(state.latitude)
          const lon = Number(state.longitude)
          if (isFinite(lat) && isFinite(lon)) {
            const { data: arpsi, error: aErr } = await supabase.rpc('check_arpsi', {
              p_lat: lat, p_lon: lon, p_buffer_m: 500,
            })
            if (!aErr) {
              if (!arpsi || arpsi.length === 0) {
                updates.arpsiStatus = 'not_at_risk (sin ARPSI en 500 m)'
                iroExtras.arpsi_status = 'not_at_risk'
              } else {
                const nearest = arpsi[0]
                updates.arpsiStatus =
                  `in_arpsi — ${nearest.name_apsfr} (${nearest.demarcacion}, ${nearest.origen_inundacion}, ${nearest.distance_m} m)`
                iroExtras.arpsi_status = 'at_risk'
              }
              iroExtras.arpsi_check_date = new Date().toISOString().slice(0, 10)
            }
          }
        }

        // Verificaciones WMS puntuales (según CCAA)
        if (hasLatLon) {
          const lat = Number(state.latitude)
          const lon = Number(state.longitude)
          if (isFinite(lat) && isFinite(lon)) {
            const ccaa = getCCAA(effectiveProv)

            // IGME BDMoves (nacional): movimientos del terreno cerca (buffer 500 m)
            const bdmoves = await checkIGMEBDMoves(lat, lon, 500)
            if (bdmoves.checked) {
              iroExtras.geotech_moves_registered = bdmoves.moves_registered
              iroExtras.geotech_check_date       = bdmoves.check_date
            }

            // IGN PGA 475 años (nacional): aceleración pico del terreno
            const pga = await checkPGA475(lat, lon)
            if (pga.checked && pga.pga_g != null) {
              iroExtras.seismic_pga_475 = pga.pga_g
            }
            // IGN Intensidad EMS-98 (475 años)
            const ems = await checkEMS98(lat, lon)
            if (ems.checked && ems.ems98 != null) {
              iroExtras.seismic_intensity_ems98 = ems.ems98
            }

            // Consultas específicas por CCAA
            if (ccaa === 'CANARIAS') {
              const zari = await checkZARI(lat, lon)
              if (zari.checked) {
                iroExtras.fire_risk_greu  = zari.in_zari
                iroExtras.fire_risk_level = zari.in_zari ? 'high' : 'low'
                iroExtras.fire_check_date = zari.check_date
              }
              const erup = await checkErupcionesHistoricas(lat, lon, 5000)
              if (erup.checked) {
                iroExtras.volcanic_eruptions_nearby = erup.eruptions_nearby
                iroExtras.volcanic_check_date       = erup.check_date
              }
            } else if (ccaa === 'CATALUÑA') {
              const perill = await checkPerillIncendi(lat, lon)
              if (perill.checked) {
                iroExtras.fire_risk_level   = (perill.level || '').toLowerCase() || null
                iroExtras.fire_in_zone      = perill.in_zone
                iroExtras.fire_level_source = perill.level_source || null
                iroExtras.fire_pixel_hex    = perill.pixel_rgb?.hex || null
                iroExtras.fire_check_date   = perill.check_date
              }
              const greu = await checkRiscGreu(lat, lon)
              if (greu.checked) {
                iroExtras.fire_risk_greu = greu.in_risc_greu
                iroExtras.fire_check_date = greu.check_date
              }
            }
          }
        }

        if (Object.keys(updates).length > 0 || Object.keys(iroExtras).length > 0) {
          updates.ubicacionLastCheck = new Date().toISOString().replace('T', ' ').slice(0, 19)
          setState(prev => ({ ...prev, ...updates }))

          // Sincronizar los valores clave también en operational_iro_settings
          // para que la pestaña Monitorización y el informe SyB 7 vean lo mismo.
          const { data: { user } } = await supabase.auth.getUser()
          if (user) {
            const iroPayload = {
              project_id: projectId, user_id: user.id,
              updated_at: new Date().toISOString(),
              ...iroExtras,  // incluye seismic, arpsi, geotech, fire, volcanic
            }
            if (hasLatLon) {
              iroPayload.latitude = Number(state.latitude)
              iroPayload.longitude = Number(state.longitude)
            }
            const muniToSave = updates.municipality || state.municipality
            if (muniToSave && muniToSave.trim().length > 2) iroPayload.municipality = muniToSave
            await supabase.from('operational_iro_settings').upsert(iroPayload, { onConflict: 'project_id' })
          }
        }
      } catch (err) {
        console.error('Auto-verify error:', err.message)
      } finally {
        setVerifying(false)
      }
    }, 2000)
    return () => { if (verifyTimer.current) clearTimeout(verifyTimer.current) }
  }, [state.latitude, state.longitude, state.municipality, state.province, projectId])

  const set = useCallback((field) => (value) => {
    userTouchedRef.current = true
    setState(prev => ({ ...prev, [field]: value }))
    setSavedOk(false)
  }, [])

  const saveNow = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    await saveDataToSupabase(projectId, state)
    setSavedOk(true)
    setTimeout(() => setSavedOk(false), 2500)
  }

  const applyExclusionsNow = async () => {
    if (!projectId) return
    setApplying(true)
    setApplyResult(null)
    try {
      // Guardar primero los datos para asegurar coherencia
      if (saveTimer.current) clearTimeout(saveTimer.current)
      await saveDataToSupabase(projectId, state)
      const result = await applyAutoExclusions(projectId, state)
      setApplyResult(result)
    } catch (err) {
      setApplyResult({ applied: [], reverted: [], skipped: [], errors: [String(err.message || err)] })
    } finally {
      setApplying(false)
    }
  }

  if (!projectId) {
    return (
      <div style={{ padding: '2rem', color: C.muted, fontFamily: FF }}>
        Selecciona un proyecto para ver los Datos del Proyecto.
      </div>
    )
  }

  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1rem' }

  return (
    <div style={{ fontFamily: FF }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        marginBottom: '1.25rem', paddingBottom: '0.875rem',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <Buildings size={26} color={C.accent} weight="duotone" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '1.15rem', fontWeight: '700', color: C.text }}>
            Datos del Proyecto
          </div>
          <div style={{ fontSize: '0.78rem', color: C.muted, marginTop: '0.15rem' }}>
            {projectName} · Campos sincronizados con la pestaña <em>Detalles de evaluación</em> de la Herramienta Maestra BREEAM ES
          </div>
        </div>
        <button onClick={applyExclusionsNow} disabled={applying}
          title="Aplica las exclusiones automáticas: marca como NO APLICA los criterios indicados por los toggles condicionales (sólo si están vacíos y sin puntos)."
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.45rem 0.9rem', background: 'transparent',
            color: C.amber, border: `1px solid ${C.amber}`,
            borderRadius: '5px', cursor: applying ? 'wait' : 'pointer',
            fontSize: '0.82rem', fontFamily: FF, fontWeight: '600',
            opacity: applying ? 0.6 : 1,
          }}>
          {applying ? 'Aplicando…' : 'Aplicar exclusiones'}
        </button>
        <button onClick={saveNow} style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          padding: '0.45rem 0.9rem', background: savedOk ? '#F5FAF0' : C.accent,
          color: savedOk ? C.olive : 'white',
          border: `1px solid ${savedOk ? C.olive : C.accent}`,
          borderRadius: '5px', cursor: 'pointer', fontSize: '0.82rem',
          fontFamily: FF, fontWeight: '600',
        }}>
          <FloppyDisk size={14} /> {savedOk ? 'Guardado' : 'Guardar ahora'}
        </button>
      </div>

      {/* Modal resultado exclusiones */}
      {applyResult && (
        <div style={{
          marginBottom: '1.25rem', padding: '1rem 1.25rem',
          background: applyResult.errors.length > 0 ? '#FDF5F3' : '#F5FAF0',
          border: `1px solid ${applyResult.errors.length > 0 ? C.warn : C.olive}`,
          borderRadius: '6px', fontSize: '0.82rem', color: C.text,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '700', marginBottom: '0.5rem' }}>
                Resultado de exclusiones automáticas
              </div>
              {applyResult.applied.length > 0 && (
                <div style={{ marginBottom: '0.4rem' }}>
                  <strong style={{ color: C.olive }}>✓ {applyResult.applied.length} criterio{applyResult.applied.length > 1 ? 's' : ''} marcado{applyResult.applied.length > 1 ? 's' : ''} NO APLICA:</strong>{' '}
                  {applyResult.applied.map(a => a.code).join(', ')}
                </div>
              )}
              {applyResult.reverted.length > 0 && (
                <div style={{ marginBottom: '0.4rem' }}>
                  <strong style={{ color: C.accent }}>↻ {applyResult.reverted.length} criterio{applyResult.reverted.length > 1 ? 's' : ''} revertido{applyResult.reverted.length > 1 ? 's' : ''} (vuelve a aplicar):</strong>{' '}
                  {applyResult.reverted.map(r => r.code).join(', ')}
                </div>
              )}
              {applyResult.skipped.length > 0 && (
                <div style={{ marginBottom: '0.4rem' }}>
                  <strong style={{ color: C.amber }}>⚠ {applyResult.skipped.length} omitido{applyResult.skipped.length > 1 ? 's' : ''}:</strong>{' '}
                  {applyResult.skipped.map(s => `${s.code} (${s.reason})`).join(' · ')}
                </div>
              )}
              {applyResult.errors.length > 0 && (
                <div style={{ marginBottom: '0.4rem', color: C.warn }}>
                  <strong>✗ Errores:</strong> {applyResult.errors.join(' · ')}
                </div>
              )}
              {applyResult.applied.length === 0 && applyResult.reverted.length === 0 && applyResult.skipped.length === 0 && applyResult.errors.length === 0 && (
                <div style={{ color: C.muted }}>Sin cambios — todos los criterios afectados ya estaban en el estado correcto.</div>
              )}
            </div>
            <button onClick={() => setApplyResult(null)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: C.muted, padding: '0.2rem 0.4rem', borderRadius: '3px', fontFamily: FF,
              }}>
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* A. Identificación */}
      <Section title="A. Identificación" icon={Buildings} count={5}
        open={openSections.identificacion}
        onToggle={() => setOpenSections(s => ({ ...s, identificacion: !s.identificacion }))}>
        <div style={grid2}>
          <Field label="Código de registro BREEAM ES"><TextInput value={state.codigoBreeam} onChange={set('codigoBreeam')} placeholder="Ej. BNC1224162" /></Field>
          <Field label="Nombre del cliente"><TextInput value={state.cliente} onChange={set('cliente')} /></Field>
          <Field label="Usuario final del edificio"><TextInput value={state.usuarioFinal} onChange={set('usuarioFinal')} /></Field>
          <Field label="Nombre del Asesor"><TextInput value={state.asesor} onChange={set('asesor')} placeholder="Ej. Gisell Duran (B-ES-649/NC)" /></Field>
          <Field label="Organización autorizada" span={2}><TextInput value={state.organizacion} onChange={set('organizacion')} placeholder="HAUS Healthy Buildings, S.L." /></Field>
        </div>
      </Section>

      {/* B. Edificio */}
      <Section title="B. Datos del edificio" icon={Buildings} count={17}
        open={openSections.edificio}
        onToggle={() => setOpenSections(s => ({ ...s, edificio: !s.edificio }))}>
        <div style={grid2}>
          <Field label="Nombre del edificio" span={2}><TextInput value={state.nombreEdificio} onChange={set('nombreEdificio')} /></Field>
          <Field label="Dirección" span={2}><TextInput value={state.direccion} onChange={set('direccion')} /></Field>
          <Field label="Código postal"><TextInput value={state.codigoPostal} onChange={set('codigoPostal')} /></Field>
          <Field label="País"><SelectInput value={state.pais} onChange={set('pais')} options={PAISES} /></Field>
          <Field label="Tipo de edificio (descripción principal)"><SelectInput value={state.tipoEdificioPrincipal} onChange={set('tipoEdificioPrincipal')} options={TIPO_EDIFICIO_PRINCIPAL} /></Field>
          <Field label="Tipo de edificio (subgrupo)"><SelectInput value={state.tipoEdificioSubgrupo} onChange={set('tipoEdificioSubgrupo')} options={TIPO_EDIFICIO_SUBGRUPO} /></Field>
          <Field label="Superficie construida (m²)"><TextInput type="number" value={state.superficieConstruida} onChange={set('superficieConstruida')} /></Field>
          <Field label="Unidades" hint="Opcional. Ej. '32 apartamentos turísticos (A2 · 12 uds + A3 · 20 uds)'. Se muestra en el Sumario del Informe de Sostenibilidad."><TextInput value={state.unidades} onChange={set('unidades')} /></Field>
          <Field label="Descripción breve del proyecto" hint="Opcional. Frase descriptiva que acompaña al nombre del edificio en el Sumario. Ej. 'un complejo residencial-turístico de alta gama ubicado en el resort Abama de Guía de Isora, Tenerife'."><TextInput value={state.descripcionProyecto} onChange={set('descripcionProyecto')} /></Field>
          <Field label="Superficie útil (m²)"><TextInput type="number" value={state.superficieUtil} onChange={set('superficieUtil')} /></Field>
          <Field label="Esquema BREEAM ES"><SelectInput value={state.esquema} onChange={set('esquema')} options={ESQUEMAS} /></Field>
          <Field label="Edición del manual técnico"><SelectInput value={state.version} onChange={set('version')} options={VERSIONES} /></Field>
          <Field label="Tipo de proyecto"><SelectInput value={state.tipoProyecto} onChange={set('tipoProyecto')} options={TIPO_PROYECTO} /></Field>
          <Field label="Fase de evaluación"><SelectInput value={state.faseEvaluacion} onChange={set('faseEvaluacion')} options={FASE_EVALUACION} /></Field>
          <Field label="Inicio de fase de diseño" hint="Opcional. Se usa en el Informe de Sostenibilidad.">
            <TextInput type="date" value={state.fechaInicioDiseno} onChange={set('fechaInicioDiseno')} />
          </Field>
          <Field label="Fin de fase de construcción" hint="Opcional. Define el rango de construcción junto a inicio de diseño.">
            <TextInput type="date" value={state.fechaFinConstruccion} onChange={set('fechaFinConstruccion')} />
          </Field>
          <Field label="Fecha de posconstrucción (final)" hint="Opcional. Fecha de emisión del informe final.">
            <TextInput type="date" value={state.fechaPostconstruccion} onChange={set('fechaPostconstruccion')} />
          </Field>
          <Field label="Tipo de localización" hint="Afecta TRA 1 (cambia escala de puntos según densidad poblacional)" span={2}>
            <SelectInput value={state.tipoLocalizacion} onChange={set('tipoLocalizacion')} options={TIPO_LOCALIZACION} />
          </Field>
          <Field label="Zona climática (CTE-DB-HE)"
            hint="Clasificación oficial española del Código Técnico de la Edificación. Ej. Tenerife = A4.">
            <SelectInput value={state.zonaClimaticaCTE}
              onChange={(v) => {
                userTouchedRef.current = true
                setState(prev => {
                  const next = { ...prev, zonaClimaticaCTE: v }
                  // Auto-sugerir Köppen SOLO si está vacío (no sobrescribimos elecciones manuales)
                  const suggested = CTE_TO_KOPPEN[v]
                  if (suggested && !prev.zonaClimatica) next.zonaClimatica = suggested
                  return next
                })
                setSavedOk(false)
              }}
              options={ZONA_CLIMATICA_CTE} />
          </Field>
          <Field label="Zona climática (Köppen-Geiger)"
            hint={state.zonaClimaticaCTE && CTE_TO_KOPPEN[state.zonaClimaticaCTE] && state.zonaClimatica !== CTE_TO_KOPPEN[state.zonaClimaticaCTE]
              ? `Sugerencia según CTE ${state.zonaClimaticaCTE}: ${CTE_TO_KOPPEN[state.zonaClimaticaCTE]}`
              : 'Clasificación mundial. Es la que se vuelca a la celda J54 del Excel maestro BREEAM.'}>
            <SelectInput value={state.zonaClimatica} onChange={set('zonaClimatica')} options={ZONA_CLIMATICA} />
          </Field>
          <Field label="Sistema de calefacción"><SelectInput value={state.sistemaCalefaccion} onChange={set('sistemaCalefaccion')} options={SISTEMA_CALEFACCION} /></Field>
          <Field label="Sistema de refrigeración"><SelectInput value={state.sistemaRefrigeracion} onChange={set('sistemaRefrigeracion')} options={SISTEMA_REFRIGERACION} /></Field>
          <Field label="Sistema de agua caliente sanitaria"><SelectInput value={state.sistemaACS} onChange={set('sistemaACS')} options={SISTEMA_ACS} /></Field>
          <Field label="Controles del edificio"><SelectInput value={state.controles} onChange={set('controles')} options={CONTROLES} /></Field>
        </div>
      </Section>

      {/* C. Ubicación geográfica */}
      <Section title="C. Ubicación geográfica" icon={Buildings} count={4}
        open={openSections.ubicacion}
        onToggle={() => setOpenSections(s => ({ ...s, ubicacion: !s.ubicacion }))}>
        <div style={grid2}>
          <Field label="Latitud (°)"
            hint="Formato decimal WGS84. Ej.: 28.16234 (positiva Norte, negativa Sur)">
            <TextInput type="number" value={state.latitude || ''}
              onChange={set('latitude')} placeholder="Ej. 28.16234" />
          </Field>
          <Field label="Longitud (°)"
            hint="Formato decimal WGS84. Ej.: -16.79876 (positiva Este, negativa Oeste)">
            <TextInput type="number" value={state.longitude || ''}
              onChange={set('longitude')} placeholder="Ej. -16.79876" />
          </Field>
          <Field label="Municipio"
            hint="Nombre del municipio según el INE. Se rellena solo si introduces lat/lon.">
            <TextInput value={state.municipality || ''}
              onChange={set('municipality')} placeholder="Ej. Guía de Isora" />
            {state.reverseGeoSuggestion?.municipality
              && state.reverseGeoSuggestion.municipality !== (state.municipality || '').trim() && (
              <div style={{ marginTop: '0.35rem', fontSize: '0.75rem' }}>
                <span style={{ color: '#7A6A5A' }}>Detectado por coordenadas: </span>
                <button type="button"
                  onClick={() => {
                    userTouchedRef.current = true
                    setState(prev => ({ ...prev, municipality: state.reverseGeoSuggestion.municipality }))
                  }}
                  style={{ background: '#F5E9D3', border: '1px solid #C98A2E', color: '#5C4A3A',
                    padding: '2px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem',
                    fontWeight: 600 }}>
                  Usar «{state.reverseGeoSuggestion.municipality}»
                </button>
              </div>
            )}
          </Field>
          <Field label="Provincia"
            hint="Provincia donde se ubica el edificio. Se rellena sola si introduces lat/lon.">
            <TextInput value={state.province || ''}
              onChange={set('province')} placeholder="Ej. Santa Cruz de Tenerife" />
            {state.reverseGeoSuggestion?.province
              && state.reverseGeoSuggestion.province !== (state.province || '').trim() && (
              <div style={{ marginTop: '0.35rem', fontSize: '0.75rem' }}>
                <span style={{ color: '#7A6A5A' }}>Detectada por coordenadas: </span>
                <button type="button"
                  onClick={() => {
                    userTouchedRef.current = true
                    setState(prev => ({ ...prev, province: state.reverseGeoSuggestion.province }))
                  }}
                  style={{ background: '#F5E9D3', border: '1px solid #C98A2E', color: '#5C4A3A',
                    padding: '2px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem',
                    fontWeight: 600 }}>
                  Usar «{state.reverseGeoSuggestion.province}»
                </button>
              </div>
            )}
          </Field>
          {(state.seismicAb || state.arpsiStatus || verifying) && (
            <Field label="Verificaciones automáticas" span={2}
              hint={verifying
                ? 'Consultando NCSE-02 (IGN) y ARPSI (MITECO SNCZI)…'
                : (state.ubicacionLastCheck ? `Última comprobación: ${state.ubicacionLastCheck}` : '')}>
              <div style={{
                display: 'flex', gap: '1rem', flexWrap: 'wrap',
                padding: '0.6rem 0.8rem',
                background: verifying ? '#FDF6E7' : '#F5F7F0',
                border: `1px solid ${verifying ? '#E5D5A0' : '#DDE3D0'}`,
                borderRadius: '4px',
                fontSize: '0.9rem',
              }}>
                {verifying && (
                  <span style={{ color: '#7A6A5A' }}>Verificando ubicación…</span>
                )}
                {!verifying && state.seismicAb && (
                  <span>
                    <strong>NCSE-02 · aceleración básica ab:</strong>{' '}
                    {state.seismicAb.startsWith('NCSE') ? state.seismicAb : `${state.seismicAb} g`}
                  </span>
                )}
                {!verifying && state.arpsiStatus && (
                  <span><strong>ARPSI (SNCZI):</strong> {state.arpsiStatus}</span>
                )}
              </div>
            </Field>
          )}
        </div>
      </Section>

      {/* D. Toggles condicionales */}
      <Section title="D. Condiciones del edificio (exclusiones automáticas)" icon={Info} count={16}
        open={openSections.toggles}
        onToggle={() => setOpenSections(s => ({ ...s, toggles: !s.toggles }))}>
        <div style={grid2}>
          <ToggleField label="¿La zona operativa del edificio industrial está calefactada/refrigerada?"
            value={state.zonaIndustrialClimatizada} onChange={set('zonaIndustrialClimatizada')} />
          <ToggleField label="¿El edificio sanitario contendrá pacientes hospitalizados?"
            value={state.edificioSanitarioPacientes} onChange={set('edificioSanitarioPacientes')} />
          <ToggleField label="¿El edificio industrial contiene una zona de oficinas?"
            value={state.industrialZonaOficinas} onChange={set('industrialZonaOficinas')} />
          <ToggleField label="¿La legislación prohíbe el uso de sustancias con PAO?"
            value={state.prohibePAO} onChange={set('prohibePAO')} />
          <ToggleField label="¿El edificio contiene inquilinos múltiples / áreas funcionales?"
            value={state.inquilinosMultiples} onChange={set('inquilinosMultiples')} affects={TOGGLE_AFFECTS.inquilinosMultiples} />
          <ToggleField label="¿Refrigeración comercial/industrial y almacenamiento en frío?"
            value={state.refrigeracionComercial} onChange={set('refrigeracionComercial')} affects={TOGGLE_AFFECTS.refrigeracionComercial} />
          <ToggleField label="¿Cuenta con zonas externas dentro del emplazamiento?"
            value={state.zonasExternas} onChange={set('zonasExternas')} affects={TOGGLE_AFFECTS.zonasExternas} span={2} />
          <ToggleField label="¿Vegetación interna/externa o jardinería blanda?"
            value={state.vegetacionPaisaje} onChange={set('vegetacionPaisaje')} affects={TOGGLE_AFFECTS.vegetacionPaisaje} />
          <ToggleField label="¿Riesgo asociado de peligros naturales (sin inundaciones)?"
            value={state.riesgoPeligrosNaturales} onChange={set('riesgoPeligrosNaturales')} affects={TOGGLE_AFFECTS.riesgoPeligrosNaturales} />
          <ToggleField label="¿Sistema de lavado de vehículos?"
            value={state.lavadoVehiculos} onChange={set('lavadoVehiculos')} affects={TOGGLE_AFFECTS.lavadoVehiculos} />
          <ToggleField label="¿Sistema de transporte de personas (ascensores/escaleras mecánicas)?"
            value={state.ascensoresEscaleras} onChange={set('ascensoresEscaleras')} affects={TOGGLE_AFFECTS.ascensoresEscaleras} />
          <ToggleField label="¿Hay piscinas interiores o exteriores?"
            value={state.piscinas} onChange={set('piscinas')} affects={TOGGLE_AFFECTS.piscinas} />
          <Field label="¿Hay laboratorios en el edificio?" hint={TOGGLE_AFFECTS.laboratorios} span={2}>
            <SelectInput value={state.laboratorios}
              onChange={(v) => {
                setState(prev => {
                  const next = { ...prev, laboratorios: v }
                  // Si "Sin laboratorio", auto-rellenar campos dependientes
                  if (v === 'Sin laboratorio') {
                    next.nivelContencionLab = 'No hay laboratorios'
                    next.dispositivosContencionLab = 'No'
                  }
                  return next
                })
                setSavedOk(false)
              }}
              options={LABORATORIOS} />
          </Field>
          <Field label="Nivel de contención del laboratorio">
            <SelectInput value={state.nivelContencionLab} onChange={set('nivelContencionLab')} options={NIVEL_CONTENCION} />
          </Field>
          <Field label="¿Hay dispositivos y zonas de contención del laboratorio?"
            hint="Si no hay laboratorios, déjalo en 'No'. Aplica solo a laboratorios de nivel 2 ó 3.">
            <SelectInput value={state.dispositivosContencionLab} onChange={set('dispositivosContencionLab')} options={SI_NO} />
          </Field>
        </div>
      </Section>

      {/* D. Equipo del proyecto */}
      <Section title="E. Equipo del proyecto" icon={Buildings} count={10}
        open={openSections.equipo}
        onToggle={() => setOpenSections(s => ({ ...s, equipo: !s.equipo }))}>
        <div style={grid2}>
          <Field label="Promotor"><TextInput value={state.promotor} onChange={set('promotor')} /></Field>
          <Field label="Constructor principal"><TextInput value={state.constructor} onChange={set('constructor')} /></Field>
          <Field label="Arquitecto"><TextInput value={state.arquitecto} onChange={set('arquitecto')} /></Field>
          <Field label="Project management"><TextInput value={state.projectManagement} onChange={set('projectManagement')} /></Field>
          <Field label="Ingeniería de instalaciones" hint="Si hay varias, sepáralas por coma (ej. IIS Ingenieros, Sycronet Ingeniería). Se vuelcan al bloque 'Promotor y Diseño' del Informe de Sostenibilidad."><TextInput value={state.instalaciones} onChange={set('instalaciones')} /></Field>
          <Field label="BREEAM AP"><TextInput value={state.breeamAP} onChange={set('breeamAP')} /></Field>
          <Field label="Otro miembro del equipo 1"><TextInput value={state.otroMiembro1} onChange={set('otroMiembro1')} /></Field>
          <Field label="Otro miembro del equipo 2"><TextInput value={state.otroMiembro2} onChange={set('otroMiembro2')} /></Field>
          <Field label="Otro miembro del equipo 3"><TextInput value={state.otroMiembro3} onChange={set('otroMiembro3')} /></Field>
          <Field label="Otro miembro del equipo 4"><TextInput value={state.otroMiembro4} onChange={set('otroMiembro4')} /></Field>
        </div>
      </Section>
    </div>
  )
}
